import { useEffect, useState, useMemo } from "react";
import { Search, Download, Users, Radar, ScrollText, AtSign, Loader2, X, Zap, Square } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AthleteCard } from "@/components/AthleteCard";
import logoFull from "@/assets/logo-full.png";
import { scanPass, etaLabel, SECONDS_PER_ATHLETE, creditRange } from "@/lib/format";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG =
  "https://images.pexels.com/photos/13001721/pexels-photo-13001721.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div
    data-testid={testid}
    className="rounded-xl border border-[#262933] bg-[#12141A] p-4 flex items-center gap-3"
  >
    <div className="h-10 w-10 rounded-lg bg-[#1A1D26] border border-[#262933] flex items-center justify-center text-[#E8833A]">
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <div className="font-display font-extrabold text-2xl text-[#F3EFE6] leading-none">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#9A9CA6] mt-1">{label}</div>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [assoc, setAssoc] = useState("");
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [scan, setScan] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    const [a, s] = await Promise.all([api.listAthletes({}), api.stats()]);
    setAthletes(a);
    setStats(s);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(() => {
      api.listAthletes({}).then(setAthletes).catch(() => {});
      api.scanStatus().then(setScan).catch(() => {});
    }, 6000);
    api.scanStatus().then(setScan).catch(() => {});
    return () => clearInterval(t);
  }, []);

  const scanActive = scan && (scan.queued > 0 || scan.current);
  const isOwner = user?.role === "admin";
  const runningAthlete = athletes.find((a) => a.discovery_status === "running");
  const currentPass = runningAthlete ? scanPass(runningAthlete.discovery_stage) : null;
  const etaSeconds = scan ? (scan.queued + (scan.current ? 1 : 0)) * SECONDS_PER_ATHLETE : 0;

  const startScanAll = async () => {
    setConfirmOpen(false);
    const r = await api.scanAll(true, discipline, assoc);
    const scope = [assoc, discipline].filter(Boolean).join(" ") || "all";
    if (r.enqueued === 0) {
      toast.info(`Every ${scope} contestant has already been scanned`);
    } else {
      toast.success(`Queued ${r.enqueued} ${scope} contestants for scanning`);
    }
    api.scanStatus().then(setScan);
  };

  const stopScanAll = async () => {
    await api.stopScan();
    toast.info("Scan queue stopped");
    api.scanStatus().then(setScan);
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await api.seedCpra();
      toast.success(`CPRA sync complete — ${r.added} added, ${r.updated} updated`);
      await load();
    } catch {
      toast.error("CPRA sync failed");
    }
    setSeeding(false);
  };

  const filtered = useMemo(() => {
    return athletes.filter((a) => {
      const okQ = !q || a.known_name.toLowerCase().includes(q.toLowerCase());
      const okD = !discipline || (a.disciplines || []).includes(discipline);
      const okA = !assoc || (a.association || "").split(/[^A-Za-z]+/).includes(assoc);
      return okQ && okD && okA;
    });
  }, [athletes, q, discipline, assoc]);

  const disciplines = stats?.disciplines || [];
  const associations = stats?.associations || [];

  const remaining = useMemo(() => {
    const scope = athletes.filter((a) =>
      (!discipline || (a.disciplines || []).includes(discipline)) &&
      (!assoc || (a.association || "").split(/[^A-Za-z]+/).includes(assoc)));
    return scope.filter((a) => !["done", "running", "queued"].includes(a.discovery_status)).length;
  }, [athletes, discipline, assoc]);
  const est = creditRange(remaining);

  return (
    <div className="max-w-[1700px] mx-auto px-5 md:px-8 py-8">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border border-[#262933] mb-8">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="rodeo" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#090A0C] via-[#090A0C]/85 to-[#090A0C]/40" />
        </div>
        <div className="relative p-7 md:p-10 max-w-3xl">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E8833A] mb-3">
            Athlete Interview Intelligence
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-[#F3EFE6] leading-[1.05]">
            Know what they've been up to
            <span className="text-[#E8833A]"> — before you ask.</span>
          </h1>
          <p className="text-[#B8BAC4] text-base mt-4 max-w-xl leading-relaxed">
            Pick any CPRA contestant and open their Interview Intel brief. We scan the public web —
            Instagram, TikTok, YouTube, podcasts, news and more — and surface verified nuggets with
            direct sources. No made-up facts. No interview questions.
          </p>
          <button
            data-testid="seed-cpra-button"
            onClick={seed}
            disabled={seeding}
            className="mt-6 flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-sm bg-[#1A1D26] border border-[#E8833A]/40 text-[#F3EFE6] hover:bg-[#22262F] transition-colors duration-200 disabled:opacity-60"
          >
            {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 text-[#E8833A]" />}
            Sync athletes from CPRA standings
          </button>
        </div>
      </section>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Athletes" value={stats?.athletes ?? "—"} testid="stat-athletes" />
        <StatCard icon={Radar} label="Scanned" value={stats?.athletes_processed ?? "—"} testid="stat-scanned" />
        <StatCard icon={ScrollText} label="Intel items" value={stats?.intel_events ?? "—"} testid="stat-intel" />
        <StatCard icon={AtSign} label="Social accounts" value={stats?.social_accounts ?? "—"} testid="stat-accounts" />
      </div>

      {/* Scan-all queue (owner only) */}
      <div className={cn("rounded-xl border border-[#262933] bg-[#12141A] p-4 mb-8", !isOwner && "hidden")} data-testid="scan-all-panel">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#1A1D26] border border-[#262933] flex items-center justify-center text-[#E8833A]">
              {scanActive ? <Loader2 className="h-5 w-5 animate-spin" /> : <Zap className="h-5 w-5" />}
            </div>
            <div>
              <div className="font-display font-bold text-[#F3EFE6]">
                {scanActive
                  ? "Scanning athletes…"
                  : discipline
                    ? `Scan ${discipline} contestants`
                    : "Build every brief"}
              </div>
              <div className="font-mono text-[11px] text-[#9A9CA6]">
                {scanActive
                  ? `${scan.processed}/${scan.total} done · ${etaLabel(etaSeconds)} left${scan.current_name ? ` · now: ${scan.current_name}${currentPass?.n ? ` (${currentPass.short} ${currentPass.n}/${currentPass.total})` : ""}` : ""}`
                  : `${remaining} ${discipline || "contestant"}${remaining === 1 ? "" : "s"} still need a scan${discipline ? "" : " across all events"}${remaining > 0 ? ` · est. ~${est.low}–${est.high} credits` : ""}`}
              </div>
            </div>
          </div>
          {scanActive ? (
            <button
              data-testid="stop-scan-button"
              onClick={stopScanAll}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm bg-[#2A1812] text-[#FB923C] border border-[#4D261B] hover:bg-[#331d15] transition-colors shrink-0"
            >
              <Square className="h-4 w-4" /> Stop
            </button>
          ) : (
            <button
              data-testid="scan-all-button"
              onClick={() => setConfirmOpen(true)}
              disabled={remaining === 0}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors shrink-0 disabled:opacity-50"
            >
              <Zap className="h-4 w-4" /> {discipline ? `Scan ${discipline}` : "Scan all remaining"}
            </button>
          )}
        </div>
        {scanActive && scan.total > 0 && (
          <div className="mt-3 h-2 rounded-full bg-[#0F1116] overflow-hidden" data-testid="scan-progress-bar">
            <div
              className="h-full bg-[#E8833A] transition-all duration-500"
              style={{ width: `${Math.round((scan.processed / scan.total) * 100)}%` }}
            />
          </div>
        )}
      </div>

      {/* Pre-scan credit estimate confirmation */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="scan-confirm-dialog" className="bg-[#12141A] border-[#262933] text-[#F3EFE6]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#F3EFE6]">
              Scan {remaining} {discipline || "contestant"}{remaining === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#9A9CA6]">
              This queues a deep 3-pass scan + presenter brief for each contestant. Estimated cost:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-lg border border-[#262933] bg-[#0F1116] p-4 my-1" data-testid="scan-estimate">
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-[11px] uppercase tracking-widest text-[#686B77]">Estimated credits</span>
              <span className="font-display font-extrabold text-2xl text-[#E8833A]">~{est.low}–{est.high}</span>
            </div>
            <div className="mt-2 flex items-center justify-between font-mono text-[11px] text-[#9A9CA6]">
              <span>{remaining} × ~2–5 credits each</span>
              <span>~{etaLabel(remaining * SECONDS_PER_ATHLETE)} to finish</span>
            </div>
            <p className="mt-3 text-[11px] text-[#686B77] leading-relaxed">
              Estimate only — actual usage depends on how much public material each athlete has.
              Already-scanned contestants are skipped. You can Stop the queue anytime.
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="scan-confirm-cancel" className="bg-[#1A1D26] border-[#262933] text-[#F3EFE6] hover:bg-[#22262F] hover:text-[#F3EFE6]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction data-testid="scan-confirm-start" onClick={startScanAll} className="bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A]">
              Start scan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="flex flex-col gap-3 mb-6">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#686B77] pointer-events-none" />
          <Input
            data-testid="athlete-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search contestants…"
            className="w-full h-11 pl-10 bg-[#12141A] border-[#262933] text-[#F3EFE6] placeholder:text-[#686B77] focus-visible:ring-[#E8833A]"
          />
        </div>
        {associations.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="association-filter">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#686B77] mr-1">Circuit</span>
            <button
              data-testid="assoc-all"
              onClick={() => setAssoc("")}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider border transition-colors duration-200",
                !assoc ? "bg-[#F3EFE6] text-[#0B0D11] border-[#F3EFE6]" : "bg-[#12141A] text-[#9A9CA6] border-[#262933] hover:text-[#F3EFE6]"
              )}
            >
              All
            </button>
            {associations.map((s) => (
              <button
                key={s}
                data-testid={`assoc-${s.toLowerCase()}`}
                onClick={() => setAssoc(assoc === s ? "" : s)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider border transition-colors duration-200",
                  assoc === s ? "bg-[#F3EFE6] text-[#0B0D11] border-[#F3EFE6]" : "bg-[#12141A] text-[#9A9CA6] border-[#262933] hover:text-[#F3EFE6]"
                )}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="filter-all"
            onClick={() => setDiscipline("")}
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider border transition-colors duration-200",
              !discipline ? "bg-[#E8833A] text-[#0B0D11] border-[#E8833A]" : "bg-[#12141A] text-[#9A9CA6] border-[#262933] hover:text-[#F3EFE6]"
            )}
          >
            All
          </button>
          {disciplines.map((d) => (
            <button
              key={d}
              data-testid={`filter-${d.replace(/[^a-z]/gi, "-").toLowerCase()}`}
              onClick={() => setDiscipline(d)}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider border transition-colors duration-200",
                discipline === d ? "bg-[#E8833A] text-[#0B0D11] border-[#E8833A]" : "bg-[#12141A] text-[#9A9CA6] border-[#262933] hover:text-[#F3EFE6]"
              )}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-24 text-[#686B77]">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading contestants…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24">
          <X className="h-8 w-8 text-[#686B77] mx-auto mb-3" />
          <p className="text-[#9A9CA6]">No athletes yet. Run the CPRA sync above to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((a) => (
            <AthleteCard key={a.id} athlete={a} />
          ))}
        </div>
      )}
    </div>
  );
}
